import React, {useState} from 'react';
import b_ from 'b_';
import {useFieldArray} from 'react-hook-form';
import get from 'lodash/get';

import ImageUploader, {UploadError, Image} from 'client/common/components/image-uploader';
import {Button} from 'client/common/components/lego';
import IconButton from 'client/common/components/icon-button';

import I18N from 'assets/i18n/brandlift-project';

import {ImageListProps} from './types';

import './creative-settings-form.css';
import './image-list.css';

const i18n = I18N.creatives.creativeSettings;
const creativeSettingsForm__ = b_.with('creative-settings-form');
const b = b_.with('image-list');

interface ImageUploadProps {
    disabled: boolean;
    defaultValue?: string;
    onUpload: (src: string) => Promise<void>;
    onClick?: ReturnType<typeof useFieldArray>['append'];
    name?: string;
    controlRef: any;
    btnText: string;
}

// eslint-disable-next-line react/display-name
const ImageUpload = (props: ImageUploadProps) => {
    const {disabled, defaultValue = '', onUpload, onClick, name, controlRef, btnText} = props;

    const [src, setSrc] = useState(defaultValue);
    const [uploadError, setUploadError] = useState('');

    async function onSuccess(data: Image | UploadError) {
        if ('error' in data) {
            setUploadError(data.error!);

            setTimeout(() => {
                setUploadError('');
            }, 5000);

            return;
        }

        if ('url' in data) {
            const {url} = data;

            setSrc(url);

            await onUpload(url);
        }
    }

    return (
        <ImageUploader onUploadSuccess={onSuccess} disabled={disabled}>
            <div className={b('image-upload-wrapper')}>
                {src && <img src={src} className={b('image-upload-img')} />}
                <input type='hidden' value={src} name={name} ref={controlRef} />
                {uploadError && <p className={b('upload-error')}>{uploadError}</p>}
                {!src && (
                    <Button
                        className={b('add-file-btn')}
                        onClick={onClick} disabled={disabled}
                        size='m'
                        view='default'
                        width='max'
                    >
                        {btnText}
                    </Button>
                )}
            </div>
        </ImageUploader>
    );
};

export default function ImageList({
    errors,
    fields,
    fieldName,
    onBlur,
    rules,
    register,
    disabled,
    onRemove,
    uploadBtnText,
    minItems,
    maxItems
}: ImageListProps) {
    const [uploadError, setUploadError] = useState('');

    async function onSuccess(data: Image | UploadError) {
        if ('error' in data) {
            setUploadError(data.error!);

            setTimeout(() => {
                setUploadError('');
            }, 5000);

            return;
        }

        if ('url' in data) {
            const {url} = data;

            await onBlur()(url);
        }
    }

    return (
        <>
            <div className={b('', {fields: fields.length ? 'yes' : 'no'})}>
                {fields.map((field, index) => {
                    const isError = get(errors, `${fieldName}.${index}.text`);

                    return (
                        <div
                            key={field.id}
                            className={b('image-upload', {error: isError ? 'yes' : 'no'})}
                        >
                            {field.text && <div key={field.id} className={b('input')}>
                                <ImageUpload
                                    controlRef={register(rules)}
                                    disabled={disabled}
                                    name={`${fieldName}[${index}].text`}
                                    onUpload={onBlur(index)}
                                    defaultValue={field.text}
                                    btnText={uploadBtnText}
                                />
                            </div>}
                            {fields.length >= minItems && (
                                <IconButton
                                    className={b('video-upload-icon', {type: 'cross'})}
                                    onClick={() => onRemove(index)}
                                    type='cross-light'
                                />
                            )}
                            {isError && (
                                <span className={`${creativeSettingsForm__('errors')} ${b('error')}`}>
                                    {get(errors, `${fieldName}.${index}.text.message`)}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
            {fields.length < maxItems && (
                <ImageUploader
                    onUploadSuccess={onSuccess}
                    disabled={disabled}
                >
                    <div className={b('image-upload-wrapper')}>
                        <Button
                            className={b('add-file-btn')}
                            disabled={disabled}
                            size='m'
                            view='default'
                            width='max'
                        >
                            {i18n.imageBtn.addText}
                        </Button>
                        {uploadError && <p className={b('upload-error')}>{uploadError}</p>}
                    </div>
                </ImageUploader>
            )}
        </>
    );
}
